import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardTitle,
  Button,
  CardBody,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faCheck,
  faArrowLeft,
  faTrophy,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import footballService from "../../services/api/football-service.js";

const truncateId = (id) => {
  if (!id || id.length < 20) return id;
  return `${id.slice(0, 10)}...${id.slice(-10)}`;
};

const TeamLogo = ({ team }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const logoUrl = `/logos/${team?.slug}.png`;

  useEffect(() => {
    if (team) setLogoFailed(false);
  }, [team]);

  return (
    <div
      className="d-flex align-items-center justify-content-center bg-light rounded-circle mx-auto mb-3"
      style={{ width: "4rem", height: "4rem", overflow: "hidden" }}
    >
      {logoFailed || !team?.slug ? (
        <span className="h4 font-weight-bold text-dark">
          {team?.code || "N/A"}
        </span>
      ) : (
        <img
          src={logoUrl}
          alt={team.name}
          onError={() => setLogoFailed(true)}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      )}
    </div>
  );
};

// --- MAIN COMPONENT ---
function TournamentDetails() {
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("teams");
  const [copiedItem, setCopiedItem] = useState(null);
  const [selectedRoundCard, setSelectedRoundCard] = useState(null);
  const { tslug } = useParams();
  const history = useHistory();
  const [showTitle, setShowTitle] = useState(false);
  const [showContent, setShowContent] = useState(false);

  const teamsTabRef = useRef(null);
  const roundsTabRef = useRef(null);

  const handleRoundClick = (roundSlug, tournamentSlug) => {
    history.replace(
      `/dashboard/football/data-center?tab=matches&tslug=${tournamentSlug}&roundSlug=${roundSlug}`
    );
  };

  const fetchTournament = useCallback(async () => {
    setLoading(true);
    setError(null);
    setShowTitle(false);
    setShowContent(false);
    try {
      const result = await footballService.getTournamentDetails(tslug);
      setTournament(result.data);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || "Tournament not found";
      setError(errorMessage);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setShowTitle(true);
        setShowContent(true);
      }, 50);
    }
  }, [tslug]);

  useEffect(() => {
    fetchTournament();
    setSelectedRoundCard(null);
  }, [fetchTournament]);

  const handleCopy = (text, itemName) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedItem(itemName);
        setTimeout(() => setCopiedItem(null), 2000);
      })
      .catch((err) => {
        console.error(`Failed to copy ${itemName}: `, err);
      });
  };

  const formatDate = (dateObj) => {
    if (!dateObj?.timestamp) return "N/A";
    return new Date(dateObj.timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const pluralize = (count, noun) =>
    `${count} ${noun}${count !== 1 ? "s" : ""}`;

  if (loading) {
    return (
      <div className="content text-center py-5">
        <Spinner color="primary" />
        <p className="mt-2">Loading tournament details...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="content text-center py-5">
        <Card className="text-danger p-4">
          <CardTitle tag="h4" className="text-danger">
            Error: {error}
          </CardTitle>
          <p>
            <Link
              to="/dashboard/football/data-center"
              className="btn btn-primary mt-3"
            >
              &larr; Back to Data Center
            </Link>
          </p>
        </Card>
      </div>
    );
  }
  if (!tournament) return null;

  return (
    <div className="content">
      <style>{`
                .round-card { transition: all 0.2s ease-in-out; cursor: pointer; }
                .round-card:hover { transform: translateY(-4px); box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
                .round-card.selected { border: 1.5px solid #007bff; transform: translateY(-4px); box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
            `}</style>
      <Row className="justify-content-center">
        <Col md="12">
          <Card>
            <CardHeader className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
              <div className="d-flex align-items-center">
                <Link
                  to="/dashboard/football/data-center"
                  className="btn btn-secondary me-3"
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                </Link>
                <div className="d-flex flex-column">
                  <CardTitle tag="h2" className="mb-0 text-left">
                    {tournament.name}
                  </CardTitle>
                  <p className="text-muted text-sm">
                    {tournament.competition.name}
                  </p>
                </div>
              </div>
              <div className="mt-3 mt-md-0">
                <div className="d-flex align-items-center mb-2">
                  <span
                    className="text-muted small fw-bold"
                    style={{ width: "50px" }}
                  >
                    Slug
                  </span>
                  <span
                    className="font-monospace bg-light px-2 py-1 rounded"
                    title={tournament.t_slug}
                  >
                    {truncateId(tournament.t_slug)}
                  </span>
                  <Button
                    id="copy-slug-button"
                    color="secondary"
                    outline
                    size="sm"
                    className="ms-2"
                    onClick={() => handleCopy(tournament.t_slug, 'slug')}
                  >
                    <FontAwesomeIcon icon={copiedItem === 'slug' ? faCheck : faCopy} />
                  </Button>
                  <UncontrolledTooltip placement="top" target="copy-slug-button">
                    {copiedItem === 'slug' ? "Copied!" : "Copy Slug"}
                  </UncontrolledTooltip>
                </div>

                {/* Key Row */}
                <div className="d-flex align-items-center">
                  <span
                    className="text-muted small fw-bold"
                    style={{ width: "50px" }}
                  >
                    Key
                  </span>
                  <span
                    className="font-monospace bg-light px-2 py-1 rounded"
                    title={tournament.key}
                  >
                    {truncateId(tournament.key)}
                  </span>
                  <Button
                    id="copy-key-button"
                    color="secondary"
                    outline
                    size="sm"
                    className="ms-2"
                    onClick={() => handleCopy(tournament.key, 'key')}
                  >
                    <FontAwesomeIcon icon={copiedItem === 'key' ? faCheck : faCopy} />
                  </Button>
                  <UncontrolledTooltip placement="top" target="copy-key-button">
                    {copiedItem === 'key' ? 'Copied!' : 'Copy Key'}
                  </UncontrolledTooltip>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              <Row className="text-center">
                <Col>
                  <p className="text-muted fs-5 mb-1 fw-bolder">Start Date</p>
                  <h4 className="text-success">
                    🗓️ {formatDate(tournament.start_date)}
                  </h4>
                </Col>
                <Col>
                  <p className="text-muted fs-5 mb-1 fw-bolder">End Date</p>
                  <h4 className="text-danger">
                    🗓️ {formatDate(tournament.end_date)}
                  </h4>
                </Col>
                <Col>
                  <p className="text-muted fs-5 mb-1 fw-bolder">Teams</p>
                  <h4 className="text-info">
                    ⚽ {pluralize(tournament.teams.length, "Team")}
                  </h4>
                </Col>
                <Col>
                  <p className="text-muted fs-5 mb-1 fw-bolder">Rounds</p>
                  <h4 className="text-warning">
                    🏆 {pluralize(tournament.rounds.length, "Round")}
                  </h4>
                </Col>
              </Row>

              <div
                className="d-flex justify-content-center border-bottom mt-4"
                style={{ marginBottom: "1rem" }}
              >
                <Button
                  innerRef={teamsTabRef}
                  color="link"
                  className={`p-0 me-4 fw-bold fs-5 ${activeTab === "teams"
                    ? "text-primary border-bottom border-primary"
                    : "text-muted"
                    }`}
                  onClick={() => {
                    setActiveTab("teams");
                    teamsTabRef.current.blur();
                  }}
                  style={{
                    borderBottomWidth: activeTab === "teams" ? "2px" : "0",
                    textDecoration: "none",
                  }}
                >
                  Teams
                </Button>
                <Button
                  innerRef={roundsTabRef}
                  color="link"
                  className={`p-0 fw-bold fs-5 ${activeTab === "rounds"
                    ? "text-primary border-bottom border-primary"
                    : "text-muted"
                    }`}
                  onClick={() => {
                    setActiveTab("rounds");
                    roundsTabRef.current.blur();
                  }}
                  style={{
                    borderBottomWidth: activeTab === "rounds" ? "2px" : "0",
                    textDecoration: "none",
                  }}
                >
                  Rounds
                </Button>
              </div>

              <div className="tab-content pt-3">
                {activeTab === "teams" && (
                  <Row className="text-center">
                    {tournament.teams.map((team) => (
                      <Col
                        key={team.slug}
                        xs="6"
                        sm="4"
                        md="3"
                        lg="2"
                        className="mb-4"
                      >
                        <Card className="p-3 shadow-sm h-100">
                          <TeamLogo team={team} />
                          <p className="fw-bold mb-0 text-truncate">
                            {team.name}
                          </p>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}

                {activeTab === "rounds" && (
                  <Row>
                    {tournament.rounds.map((round) => (
                      <Col key={round.r_slug} md="6" lg="4" className="mb-3">
                        {/* CHANGED: onClick now passes the round slug and tournament slug */}
                        <Card
                          className={`round-card h-100 ${selectedRoundCard === round.r_slug ? "selected" : ""
                            }`}
                          onClick={() =>
                            handleRoundClick(round.r_slug, tournament.t_slug)
                          }
                        >
                          <CardBody className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <FontAwesomeIcon
                                icon={faTrophy}
                                className="text-warning me-3"
                                size="lg"
                              />
                              <h5 className="mb-0">{round.name}</h5>
                            </div>
                            <FontAwesomeIcon
                              icon={faChevronRight}
                              className="text-muted"
                            />
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default TournamentDetails;